import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Gavel, Target, Users, Scale, FileSignature, Trash2, XCircle, ShieldAlert } from 'lucide-react';

const Notch = ({ corner }) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    transition={{ duration: 0.5, delay: 0.3 }}
    className={`absolute w-2 h-2 md:w-3 md:h-3 border-orange-500 z-10 
      ${corner === 'top-left' ? 'top-0 left-0 border-t-2 border-l-2' : ''}
      ${corner === 'top-right' ? 'top-0 right-0 border-t-2 border-r-2' : ''}
      ${corner === 'bottom-left' ? 'bottom-0 left-0 border-b-2 border-l-2' : ''}
      ${corner === 'bottom-right' ? 'bottom-0 right-0 border-b-2 border-r-2' : ''}
    `}
  />
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const PrivacySection = ({ icon: Icon, title, children }) => (
  <motion.div 
    variants={itemVariants} 
    className="bg-white p-5 md:p-7 rounded-xl md:rounded-2xl shadow-sm border border-slate-100 mb-4 md:mb-6 relative group"
  >
    <Notch corner="top-left" />
    <Notch corner="bottom-right" />
    
    <div className="flex flex-row items-start gap-4 md:gap-5 relative z-10">
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-slate-950 flex items-center justify-center flex-shrink-0">
        <Icon className="text-orange-500" size={20} />
      </div>
      <div className="flex-1">
        <h2 className="text-lg md:text-xl font-black text-slate-950 uppercase italic tracking-tighter mb-2 md:mb-4">
          {title}
        </h2>
        <div className="text-slate-600 text-xs md:text-sm leading-relaxed space-y-2 md:space-y-3 font-medium">
          {children}
        </div>
      </div>
    </div>
  </motion.div>
);

const DPDPRightsBlock = ({ icon: Icon, title, desc }) => (
    <div className="bg-slate-50 p-3 md:p-4 rounded-lg md:rounded-xl border border-slate-100">
        <div className="flex items-center gap-2 mb-1.5">
            <Icon className="text-orange-500" size={14}/>
            <h4 className="font-mono text-[9px] md:text-[11px] uppercase font-black text-slate-900 tracking-wider">
                {title}
            </h4>
        </div>
        <p className="text-slate-500 text-[10px] md:text-[11px] leading-tight md:leading-relaxed">
            {desc}
        </p>
    </div>
)

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

    useEffect(() => {
      window.scrollTo(0, 0);
      document.title = "Marvel Sato | Privacy Policy";
    }, []);

  return (
    <div className="bg-[#f8fafc] min-h-screen font-sans relative overflow-x-hidden">
      
      <motion.div
        initial={{ y: '0%' }}
        animate={{ y: '100%' }}
        transition={{ duration: 1.5, ease: 'easeInOut' }}
        className="fixed inset-x-0 top-0 h-1 bg-orange-500/80 z-[60] pointer-events-none"
      />

      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#1e293b 0.5px, transparent 0.5px)', backgroundSize: '16px 16px' }} />

      {/* HERO SECTION - Adjusted for Mobile height/spacing */}
      <div className="bg-slate-950 pt-12 pb-14 md:pt-16 md:pb-20 px-5 md:px-6 relative overflow-hidden border-b-4 border-orange-500">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6 md:gap-4">
          <div className="relative z-10">
            <h1 className="text-3xl md:text-5xl font-black text-white uppercase italic tracking-tighter mb-1">
              Data <span className="text-orange-500">.</span> Privacy
            </h1>
            <p className="text-slate-400 font-bold tracking-[0.2em] uppercase text-[9px] md:text-xs">
              MARVEL SATO 2026
            </p>
          </div>
          
          <div className="flex items-center self-start md:self-center gap-3 bg-slate-800/50 px-4 py-2.5 md:px-5 md:py-3 rounded-full border border-slate-700">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <div className="flex flex-col">
                <p className="text-[8px] text-slate-500 font-black uppercase tracking-widest leading-none">Status</p>
                <p className="text-white font-mono text-[10px] md:text-[11px] uppercase font-bold">Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT AREA */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="max-w-4xl mx-auto px-5 md:px-6 py-8 md:py-16 relative z-10"
      >
        <p className="font-mono text-[10px] md:text-[11px] text-slate-400 mb-6 md:mb-8 italic uppercase tracking-tighter">
            Ref: MS-DPDP-2026-V1
        </p>

        <PrivacySection icon={Gavel} title="Core Mandate">
          <p>
            Marvel Engineering Private Limited ("Marvel Sato") operates under ISO 9001:2015. 
            Our commitment to data mirrors our lifting precision.
          </p>
          <p>
            Utilizing `marvelsato.com` constitutes consent to our handling methodologies, 
            structured under India’s **DPDP Act (2026)**.
          </p>
        </PrivacySection>

        <PrivacySection icon={Target} title="Data Capture">
          <p>Required for industrial project assessment:</p>
          <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100 font-black uppercase text-slate-800 text-[9px] md:text-[10px] tracking-tight">
            <span className="flex items-center gap-2"><div className="w-1 h-1 bg-orange-500"/> Identity: Name, Company</span>
            <span className="flex items-center gap-2"><div className="w-1 h-1 bg-orange-500"/> Contact: Email, Phone</span>
            <span className="flex items-center gap-2"><div className="w-1 h-1 bg-orange-500"/> Location: Project City</span>
            <span className="flex items-center gap-2"><div className="w-1 h-1 bg-orange-500"/> Specs: Machine Requirements</span>
          </div>
        </PrivacySection>

        {/* DPDP ACT MATRIX - Responsive Grid */}
        <motion.div variants={itemVariants} className="bg-white p-5 md:p-7 rounded-xl md:rounded-2xl shadow-sm border border-slate-100 mb-4 md:mb-6 relative overflow-hidden group">
            <Notch corner="top-right" />
            <Notch corner="bottom-left" />
            <h2 className="text-lg md:text-xl font-black text-slate-950 uppercase italic tracking-tighter mb-4 md:mb-6 flex items-center gap-2">
                DPDP <span className="text-orange-500">Rights</span> <ShieldAlert size={16}/>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                <DPDPRightsBlock icon={Scale} title="Correction" desc="Update inaccurate company or contact details." />
                <DPDPRightsBlock icon={Trash2} title="Erasure" desc="Request deletion once project logs expire." />
                <DPDPRightsBlock icon={XCircle} title="Withdrawal" desc="Revoke consent for future communications." />
                <DPDPRightsBlock icon={Users} title="Access" desc="Request a summary of your processed data." />
            </div>
        </motion.div>

        <PrivacySection icon={FileSignature} title="Retention">
          <p>
            We retain machinery lifecycle logs for **one year** post-contract termination. 
            Inquiry data is purged every **six months** to maintain security hygiene.
          </p>
        </PrivacySection>
        
      </motion.div>

      <footer className="py-8 md:py-12 bg-white text-center border-t border-slate-100 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[10px] md:text-[11px] text-slate-400 font-mono tracking-tighter">marvelhpt02@gmail.com</p>
          <p className="text-slate-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest mt-2">
            &copy; 2026 Marvel Sato. Engineering Lifting Excellence.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;